import type {Editor} from '@tiptap/core';
import type {Mark, MarkType, Node as ProseMirrorNode} from '@tiptap/pm/model';
import type {EditorState, Transaction} from '@tiptap/pm/state';
import type {HtmlAttributes} from './rich-text-schema';

/**
 * The editor's formatting, written the way CKEditor 4 wrote it, on the htmlInline marks of
 * rich-text-schema. Each format is a style property or a tag:
 * - with `inlineStyles` (the widget texts' coreStyles), bold is `<span style="font-weight:bold;">`,
 *   italic `font-style:italic;`, underline `text-decoration:underline;`, strike-through
 *   `text-decoration:line-through;`; otherwise `<strong>`, `<em>`, `<u>`, `<s>`;
 * - font size, family, colour and background colour are always a span with that one property
 *   (`<span style="font-size:18px;">`), replacing the property where the text already had it.
 *
 * Whether text has a format is read the way it renders: from the outermost element in, the last
 * element that says something about it wins (`<b><span style="font-weight:normal">` is not bold).
 * Turning a format off removes it from the elements that give it (a tag with no attributes goes,
 * a style loses the property and goes when nothing is left), so text never ends up wrapped twice.
 */

export type ToggleFormat = 'bold' | 'italic' | 'underline' | 'strike';
export type StyleProperty = 'font-size' | 'font-family' | 'color' | 'background-color';

interface FormatSpec {
  tags: string[];
  property: string;
  on: (value: string) => boolean;
  style: string;
  tag: string;
}

const FORMATS: Record<ToggleFormat, FormatSpec> = {
  bold: {
    tags: ['b', 'strong'], property: 'font-weight', style: 'font-weight:bold;', tag: 'strong',
    on: (value) => /^(bold|bolder|[6-9]00)$/i.test(value),
  },
  italic: {
    tags: ['i', 'em'], property: 'font-style', style: 'font-style:italic;', tag: 'em',
    on: (value) => /^(italic|oblique)/i.test(value),
  },
  underline: {
    tags: ['u', 'ins'], property: 'text-decoration', style: 'text-decoration:underline;', tag: 'u',
    on: (value) => /underline/i.test(value),
  },
  strike: {
    tags: ['s', 'strike', 'del'], property: 'text-decoration', style: 'text-decoration:line-through;', tag: 's',
    on: (value) => /line-through/i.test(value),
  },
};


export interface StyleDeclaration {
  property: string;
  value: string;
}

export const parseStyle = (style: string): StyleDeclaration[] =>
  String(style || '').split(';').map((declaration) => {
    const at = declaration.indexOf(':');
    return at < 0 ? null : {property: declaration.slice(0, at).trim().toLowerCase(), value: declaration.slice(at + 1).trim()};
  }).filter((declaration) => declaration && declaration.property);

const writeStyle = (declarations: StyleDeclaration[]): string =>
  declarations.map((declaration) => `${declaration.property}:${declaration.value};`).join(' ');

const styleValue = (mark: Mark, property: string): string | null => {
  const found = parseStyle(mark.attrs['attributes']?.['style']).filter((declaration) => declaration.property === property);
  return found.length ? found[found.length - 1].value : null;
};

/** Whether a text's marks give it the format (see above). */
export const hasFormat = (marks: readonly Mark[], format: ToggleFormat): boolean => {
  const spec = FORMATS[format];
  let on = false;
  for (const mark of marks) {
    if (mark.type.name !== 'htmlInline') {
      continue;
    }
    if (spec.tags.includes(mark.attrs['tag'])) {
      on = true;
    }
    const value = styleValue(mark, spec.property);
    if (value !== null) {
      on = spec.on(value);
    }
  }
  return on;
};

/** The value a text's marks give a style property, innermost first, or null. */
export const styleOf = (marks: readonly Mark[], property: StyleProperty): string | null => {
  let found: string | null = null;
  for (const mark of marks) {
    if (mark.type.name === 'htmlInline') {
      found = styleValue(mark, property) ?? (property === 'font-family' && mark.attrs['tag'] === 'font' ? mark.attrs['attributes']?.['face'] ?? found : found);
      if (property === 'color' && mark.attrs['tag'] === 'font' && mark.attrs['attributes']?.['color']) {
        found = mark.attrs['attributes']['color'];
      }
    }
  }
  return found;
};

const inlineType = (state: EditorState): MarkType => state.schema.marks['htmlInline'];

const isLink = (mark: Mark) => mark.type.name === 'htmlInline' && mark.attrs['tag'] === 'a';

const markWith = (state: EditorState, tag: string, attributes: HtmlAttributes): Mark =>
  inlineType(state).create({tag, attributes});

/**
 * The mark without what gives `property` (or the format's tags): null when nothing of it is left.
 * `keep` decides, for a style property, which declarations stay.
 */
const withoutProperty = (state: EditorState, mark: Mark, property: string, tags: string[] = [],
                         keep: (declaration: StyleDeclaration) => boolean = () => false): Mark | null => {
  const attributes: HtmlAttributes = {...mark.attrs['attributes']};
  let tag: string = mark.attrs['tag'];
  if (attributes['style'] !== undefined) {
    const declarations = parseStyle(attributes['style']);
    const left = declarations.filter((declaration) => declaration.property !== property || keep(declaration));
    if (left.length !== declarations.length) {
      if (left.length) {
        attributes['style'] = writeStyle(left);
      } else {
        delete attributes['style'];
      }
    }
  }
  if (tags.includes(tag)) {
    tag = 'span';
  }
  if (property === 'font-family' && tag === 'font') {
    delete attributes['face'];
  }
  if (property === 'color' && tag === 'font') {
    delete attributes['color'];
  }
  const unchanged = tag === mark.attrs['tag'] && JSON.stringify(attributes) === JSON.stringify(mark.attrs['attributes']);
  if (unchanged) {
    return mark;
  }
  if ((tag === 'span' || tag === 'font') && !Object.keys(attributes).length) {
    return null;
  }
  return markWith(state, tag, attributes);
};

/** Rewrites the marks of every text node between from and to with `rewrite`. */
const rewriteMarks = (tr: Transaction, from: number, to: number,
                      rewrite: (marks: readonly Mark[]) => readonly Mark[]): void => {
  const segments: Array<{from: number; to: number; marks: readonly Mark[]}> = [];
  tr.doc.nodesBetween(from, to, (node: ProseMirrorNode, position: number) => {
    if (!node.isInline) {
      return true;
    }
    const start = Math.max(position, from);
    const end = Math.min(position + node.nodeSize, to);
    if (start < end) {
      segments.push({from: start, to: end, marks: node.marks});
    }
    return false;
  });
  const type = tr.doc.type.schema.marks['htmlInline'];
  for (const segment of segments) {
    const next = rewrite(segment.marks);
    if (next.length === segment.marks.length && next.every((mark, index) => mark.eq(segment.marks[index]))) {
      continue;
    }
    tr.removeMark(segment.from, segment.to, type);
    for (const mark of next) {
      if (mark.type === type) {
        tr.addMark(segment.from, segment.to, mark);
      }
    }
  }
};

const selectionMarks = (state: EditorState): readonly Mark[] =>
  state.storedMarks || state.selection.$from.marks();

/** Whether all the selected text (or the text typed next) has the format. */
export const isFormatActive = (state: EditorState, format: ToggleFormat): boolean => {
  const {from, to, empty} = state.selection;
  if (empty) {
    return hasFormat(selectionMarks(state), format);
  }
  let all = true;
  let any = false;
  state.doc.nodesBetween(from, to, (node) => {
    if (node.isText) {
      any = true;
      all = all && hasFormat(node.marks, format);
    }
  });
  return any && all;
};

export const toggleFormat = (editor: Editor, format: ToggleFormat, inlineStyles: boolean): boolean => {
  const {state, view} = editor;
  const spec = FORMATS[format];
  const turnOff = isFormatActive(state, format);
  const keepOther = (declaration: StyleDeclaration) => spec.property === 'text-decoration' && !spec.on(declaration.value);
  const off = (marks: readonly Mark[]) => marks
    .map((mark) => mark.type.name === 'htmlInline' && (spec.tags.includes(mark.attrs['tag']) || styleValue(mark, spec.property) !== null)
      ? withoutProperty(state, mark, spec.property, spec.tags, keepOther)
      : mark)
    .filter(Boolean);
  const added = inlineStyles ? markWith(state, 'span', {style: spec.style}) : markWith(state, spec.tag, {});
  const tr = state.tr;
  const {from, to, empty} = state.selection;
  if (empty) {
    const marks = selectionMarks(state);
    tr.setStoredMarks(turnOff ? off(marks) : [...marks, added]);
  } else if (turnOff) {
    rewriteMarks(tr, from, to, off);
  } else {
    rewriteMarks(tr, from, to, (marks) => hasFormat(marks, format) ? marks : [...marks, added]);
  }
  view.dispatch(tr);
  return true;
};

/** Sets a style property on the selection (or what is typed next); null removes it. */
export const setStyleProperty = (editor: Editor, property: StyleProperty, value: string | null): boolean => {
  const {state, view} = editor;
  const strip = (marks: readonly Mark[]) => marks
    .map((mark) => mark.type.name === 'htmlInline' ? withoutProperty(state, mark, property) : mark)
    .filter(Boolean);
  const added = value ? markWith(state, 'span', {style: `${property}:${value};`}) : null;
  const tr = state.tr;
  const {from, to, empty} = state.selection;
  if (empty) {
    const marks = strip(selectionMarks(state));
    tr.setStoredMarks(added ? [...marks, added] : marks);
  } else {
    rewriteMarks(tr, from, to, (marks) => added ? [...strip(marks), added] : strip(marks));
  }
  view.dispatch(tr);
  return true;
};

export const removeFormat = (editor: Editor): boolean => {
  const {state, view} = editor;
  const tr = state.tr;
  const {from, to, empty} = state.selection;
  if (empty) {
    tr.setStoredMarks(selectionMarks(state).filter((mark) => mark.type.name !== 'htmlInline' || isLink(mark)));
  } else {
    rewriteMarks(tr, from, to, (marks) => marks.filter((mark) => mark.type.name !== 'htmlInline' || isLink(mark)));
  }
  view.dispatch(tr);
  return true;
};

/** Where the run of text carrying `mark` that touches `position` starts and ends. */
const runAround = (state: EditorState, position: number, mark: Mark): {from: number; to: number} | null => {
  const $position = state.doc.resolve(position);
  const start = $position.start();
  let runFrom = -1;
  let found: {from: number; to: number} | null = null;
  $position.parent.forEach((child, offset) => {
    const childFrom = start + offset;
    const childTo = childFrom + child.nodeSize;
    if (!mark.isInSet(child.marks)) {
      runFrom = -1;
      return;
    }
    if (runFrom < 0) {
      runFrom = childFrom;
    }
    if (found && found.to === childFrom) {
      found.to = childTo;
    } else if (!found && runFrom <= position && position <= childTo) {
      found = {from: runFrom, to: childTo};
    }
  });
  return found;
};

/** The link at the cursor or at the start of the selection: its mark and where it runs. */
export const linkAt = (state: EditorState): {mark: Mark; from: number; to: number} | null => {
  const {$from} = state.selection;
  const mark = [...$from.marks(), ...($from.nodeAfter?.marks || [])].find(isLink);
  const range = mark && runAround(state, $from.pos, mark);
  return range ? {mark, ...range} : null;
};

/**
 * Links the selection to `href`, or changes the link the cursor is in. `target` _blank opens it in
 * a new tab; null leaves no target. A new link is `<a href target>`, as CKEditor wrote it.
 */
export const setLink = (editor: Editor, href: string, target: string | null, text?: string): boolean => {
  const {state, view} = editor;
  const tr = state.tr;
  const existing = linkAt(state);
  if (existing) {
    const attributes: HtmlAttributes = {...existing.mark.attrs['attributes'], href};
    if (target) {
      attributes['target'] = target;
    } else {
      delete attributes['target'];
    }
    rewriteMarks(tr, existing.from, existing.to, (marks) => marks.map((mark) => mark.eq(existing.mark)
      ? markWith(state, 'a', attributes) : mark));
    view.dispatch(tr);
    return true;
  }
  const attributes: HtmlAttributes = target ? {href, target} : {href};
  const link = markWith(state, 'a', attributes);
  const {from, to, empty} = state.selection;
  if (empty) {
    const inserted = text || href;
    tr.insert(from, state.schema.text(inserted, [...selectionMarks(state), link]));
  } else {
    rewriteMarks(tr, from, to, (marks) => [...marks, link]);
  }
  view.dispatch(tr);
  return true;
};

export const unsetLink = (editor: Editor): boolean => {
  const {state, view} = editor;
  const existing = linkAt(state);
  const {from, to} = existing || state.selection;
  const tr = state.tr;
  rewriteMarks(tr, from, to, (marks) => marks.filter((mark) => !isLink(mark)));
  view.dispatch(tr);
  return true;
};

/**
 * Underline on a link (the widget texts): the link's own text-decoration goes between none and
 * underline instead of wrapping it, as the widget editors did in CKEditor (links are underlined
 * by their style, so a second underline could not be taken off).
 */
export const toggleLinkUnderline = (editor: Editor): boolean => {
  const {state, view} = editor;
  const existing = linkAt(state);
  if (!existing) {
    return false;
  }
  const declarations = parseStyle(existing.mark.attrs['attributes']?.['style']);
  const current = declarations.find((declaration) => declaration.property === 'text-decoration');
  const next = current && /none/i.test(current.value) ? 'underline' : 'none';
  const left = declarations.filter((declaration) => declaration.property !== 'text-decoration');
  const attributes = {...existing.mark.attrs['attributes'], style: writeStyle([...left, {property: 'text-decoration', value: next}])};
  const tr = state.tr;
  rewriteMarks(tr, existing.from, existing.to, (marks) => marks.map((mark) => mark.eq(existing.mark)
    ? markWith(state, 'a', attributes) : mark));
  view.dispatch(tr);
  return true;
};

/** Inserts text at the cursor, with the formatting around it. */
export const insertText = (editor: Editor, text: string): boolean => {
  const {state, view} = editor;
  const {from, to} = state.selection;
  view.dispatch(state.tr.insertText(text, from, to).scrollIntoView());
  return true;
};

/**
 * Indent or outdent the paragraphs and headings the selection touches by `step` pixels of
 * margin-left, as CKEditor's indentblock did (40px a step); none left removes the property.
 */
export const indentBlocks = (editor: Editor, step: number): boolean => {
  const {state, view} = editor;
  const tr = state.tr;
  const {from, to} = state.selection;
  state.doc.nodesBetween(from, to, (node, position) => {
    if (!node.isTextblock || node.type.name === 'listItem') {
      return true;
    }
    const attributes: HtmlAttributes = {...(node.attrs['htmlAttributes'] || {})};
    const declarations = parseStyle(attributes['style']);
    const current = declarations.find((declaration) => declaration.property === 'margin-left');
    const margin = Math.max(0, (parseFloat(current?.value) || 0) + step);
    const left = declarations.filter((declaration) => declaration.property !== 'margin-left');
    const next = margin ? [...left, {property: 'margin-left', value: `${margin}px`}] : left;
    if (next.length) {
      attributes['style'] = writeStyle(next);
    } else {
      delete attributes['style'];
    }
    tr.setNodeMarkup(position, undefined, {...node.attrs, htmlAttributes: attributes});
    return false;
  });
  view.dispatch(tr);
  return true;
};

const blockSpanOf = (marks: readonly Mark[]): Mark | null =>
  [...marks].reverse().find((mark) => mark.type.name === 'htmlInline' && mark.attrs['tag'] === 'span'
    && /display\s*:\s*block/i.test(mark.attrs['attributes']?.['style'] || '')) || null;

/** The alignment of the line (inline text) or block the cursor is in, or null. */
export const alignmentAt = (state: EditorState): string | null => {
  const {$from} = state.selection;
  if (state.doc.isTextblock) {
    const span = blockSpanOf([...$from.marks(), ...($from.nodeAfter?.marks || [])]);
    return span ? styleValue(span, 'text-align') : null;
  }
  return parseStyle($from.parent.attrs['htmlAttributes']?.['style'])
    .filter((declaration) => declaration.property === 'text-align').pop()?.value ?? null;
};

const withStyleProperty = (attributes: HtmlAttributes, property: string, value: string): HtmlAttributes => {
  const declarations = parseStyle(attributes['style']).filter((declaration) => declaration.property !== property);
  return {...attributes, style: writeStyle([...declarations, {property, value}])};
};

/**
 * Aligns the line the cursor is in. In an inline text a line is a block span (the share page writes
 * each line as `<span style="display: block">`): the span the line is in gets text-align, or the
 * text between the line breaks around the cursor becomes one. In a paragraph-based text the
 * paragraphs and headings of the selection get it.
 */
export const alignLine = (editor: Editor, value: 'left' | 'center' | 'right'): boolean => {
  const {state, view} = editor;
  const tr = state.tr;
  const {$from} = state.selection;
  if (!state.doc.isTextblock) {
    state.doc.nodesBetween(state.selection.from, state.selection.to, (node, position) => {
      if (!node.isTextblock || node.type.name === 'listItem') {
        return true;
      }
      tr.setNodeMarkup(position, undefined, {
        ...node.attrs,
        htmlAttributes: withStyleProperty(node.attrs['htmlAttributes'] || {}, 'text-align', value),
      });
      return false;
    });
    view.dispatch(tr);
    return true;
  }
  const span = blockSpanOf([...$from.marks(), ...($from.nodeAfter?.marks || [])]);
  if (span) {
    const range = runAround(state, $from.pos, span);
    const aligned = markWith(state, 'span', withStyleProperty(span.attrs['attributes'], 'text-align', value));
    rewriteMarks(tr, range.from, range.to, (marks) => marks.map((mark) => mark.eq(span) ? aligned : mark));
  } else {
    let from = 0;
    let to = state.doc.content.size;
    state.doc.forEach((node, offset) => {
      if (node.type.name !== 'hardBreak') {
        return;
      }
      if (offset < $from.pos) {
        from = offset + node.nodeSize;
      } else if (offset >= $from.pos && to === state.doc.content.size) {
        to = offset;
      }
    });
    const line = markWith(state, 'span', {style: `display: block; text-align: ${value};`});
    rewriteMarks(tr, from, to, (marks) => [line, ...marks]);
  }
  view.dispatch(tr);
  return true;
};
